import React, { useContext, useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import { ThemeContext } from '../../context/ThemeContext';
import { authGet } from '../../utils/authStorage';

// Role-based menu visibility map
const ROLE_MENU_ACCESS = {
  owner: ['Dashboard', 'Organizations', 'Users', 'Team', 'Templates', 'Billing', 'Platform', 'System'],
  developer: ['Dashboard', 'Templates', 'Platform', 'System'],
  support: ['Dashboard', 'Organizations', 'Users'],
  sales: ['Dashboard', 'Organizations', 'Billing'],
  accounts: ['Dashboard', 'Billing'],
};

const Sidebar = ({ isExpanded, setIsExpanded }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);

  // Signed-in member's console role
  const platformRole = authGet('platform_role') || 'owner';

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [openMenus, setOpenMenus] = useState(() => {
    try {
      const saved = sessionStorage.getItem('ac_sidebarMenus');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const closeOnPhone = () => {
    if (isMobile) setIsExpanded(false);
  };

  const toggleMenu = (label) => {
    setOpenMenus((prev) => {
      const updated = {};
      if (!prev[label]) updated[label] = true;
      sessionStorage.setItem('ac_sidebarMenus', JSON.stringify(updated));
      return updated;
    });
  };

  const menu = [
    {
      label: 'Dashboard',
      icon: 'hugeicons:dashboard-square-01',
      children: [{ to: '/dashboard', icon: 'hugeicons:dashboard-square-01', label: 'Overview' }]
    },
    {
      label: 'Organizations',
      icon: 'solar:global-linear',
      children: [
        { to: '/organizations', icon: 'solar:documents-outline', label: 'All Organizations' },
        { to: '/organizations/create', icon: 'solar:global-linear', label: 'Create Organization' },
      ]
    },
    {
      label: 'Users',
      icon: 'solar:users-group-rounded-linear',
      children: [{ to: '/users', icon: 'solar:user-speak-linear', label: 'All Users' }]
    },
    {
      label: 'Team',
      icon: 'solar:shield-user-linear',
      children: [{ to: '/team', icon: 'solar:shield-user-linear', label: 'Team Management' }]
    },
    {
      label: 'Templates',
      icon: 'solar:document-text-linear',
      children: [
        { to: '/templates', icon: 'solar:documents-outline', label: 'Template Library' },
        { to: '/templates/create', icon: 'solar:add-circle-linear', label: 'Create Template' },
        // CRM-backed (crm.leadspoint.in)
        { to: '/templates/lob-manager', icon: 'solar:layers-linear', label: 'LOB Templates (CRM)' },
        { to: '/field-options', icon: 'solar:list-check-linear', label: 'Field Options (CRM)' },
        { to: '/template-assignment', icon: 'solar:share-linear', label: 'Assign to Orgs (CRM)' },
      ]
    },
    {
      label: 'Billing',
      icon: 'solar:money-bag-linear',
      children: [
        { to: '/billing/overview', icon: 'solar:chart-outline', label: 'Billing Overview' },
        { to: '/billing/plans', icon: 'solar:money-bag-linear', label: 'Billing Plans' },
        { to: '/billing/services', icon: 'solar:widget-add-linear', label: 'Service Modules' },
        { to: '/billing/subscriptions', icon: 'solar:user-check-outline', label: 'Subscriptions' },
        { to: '/billing/invoices', icon: 'solar:bill-list-linear', label: 'All Invoices' },
        { to: '/billing/promo-codes', icon: 'solar:ticket-linear', label: 'Promo Codes' },
        { to: '/billing/company-settings', icon: 'solar:settings-linear', label: 'Company Settings' },
        { to: '/billing/payment-gateway', icon: 'solar:card-linear', label: 'Payment Gateway' },
      ]
    },
    {
      label: 'Platform',
      icon: 'solar:server-path-linear',
      children: [
        { to: '/platform/audit-logs', icon: 'solar:shield-check-outline', label: 'Audit Logs' },
      ]
    },
    {
      label: 'System',
      icon: 'solar:server-square-linear',
      // Served by the CRM backend. Server Monitor stays hidden: it has no API.
      children: [
        { to: '/system/telecom', icon: 'solar:phone-calling-rounded-linear', label: 'Telecom (CRM)' },
        { to: '/system/reviews', icon: 'solar:star-linear', label: 'Reviews (CRM)' },
      ]
    },
  ];

  return (
    <motion.aside
      initial={{ x: -80, opacity: 0 }}
      animate={{
        x: isMobile && !isExpanded ? '-100%' : 0,
        opacity: 1,
      }}
      transition={{ type: 'spring', stiffness: 80, damping: 20 }}
      className={`fixed top-0 left-0 h-full z-50 flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-[width] duration-300 overflow-visible
        ${isExpanded ? 'w-64' : 'w-16'}
      `}
    >
      {/* Header */}
      <div className="relative flex items-center px-4 py-6 border-b border-gray-200 dark:border-gray-700">
        <div className={`flex items-center gap-2 ${!isExpanded ? 'justify-center w-full' : ''}`}>
          <div className="h-8 w-8 rounded-lg bg-primary-black flex items-center justify-center flex-shrink-0">
            <Icon icon="solar:settings-linear" className="w-5 h-5 text-white" />
          </div>
          {isExpanded && (
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              Admin Console
            </span>
          )}
        </div>

        {/* Phone: close the menu from inside it */}
        {isMobile && isExpanded && (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            aria-label="Close menu"
            className="ml-auto h-10 w-10 rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <Icon icon="solar:close-circle-linear" className="h-6 w-6" aria-hidden="true" />
          </button>
        )}

        {/* Toggle button */}
        <button
          type="button"
          onClick={() => setIsExpanded((p) => !p)}
          title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={isExpanded}
          className="hidden md:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-50
            h-7 w-7 rounded-full bg-white dark:bg-gray-900
            border border-gray-200 dark:border-gray-700 shadow-md
            items-center justify-center
            hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
        >
          <Icon
            icon={isExpanded ? 'solar:alt-arrow-left-linear' : 'solar:alt-arrow-right-linear'}
            className="h-3.5 w-3.5 text-gray-500"
          />
        </button>
      </div>

      {/* Navigation */}
      <nav aria-label="Main" className="flex-1 px-3 py-2 space-y-2 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
        {menu
          .filter((section) => {
            const key = section.label.toLowerCase();
            const user = JSON.parse(authGet('admin_console_user') || '{}');
            const perms = user.permissions || {};

            // Check if there's a specific permission override
            if (perms[key] && perms[key].view !== undefined) {
              return perms[key].view;
            }

            // Fall back to role defaults
            const allowedMenus = ROLE_MENU_ACCESS[platformRole] || ROLE_MENU_ACCESS.owner;
            return allowedMenus.includes(section.label);
          })
          .map((section) => {
          const hasMultipleItems = section.children.length > 1;
          const isActive = section.children.some((child) => pathname === child.to);
          const firstChild = section.children[0];

          return (
            <div key={section.label}>
              {hasMultipleItems ? (
                <>
                  <button
                    type="button"
                    title={isExpanded ? undefined : section.label}
                    aria-label={section.label}
                    aria-expanded={isExpanded ? !!openMenus[section.label] : false}
                    onClick={() => {
                      if (isExpanded) {
                        toggleMenu(section.label);
                      } else {
                        setIsExpanded(true);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                      isActive
                        ? 'bg-gray-100 dark:bg-gray-800/60 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    } ${!isExpanded ? 'justify-center' : ''}`}
                  >
                    <span className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-black text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                    }`}>
                      <Icon icon={section.icon} className="h-5 w-5" aria-hidden="true" />
                    </span>
                    {isExpanded && (
                      <>
                        <span className="text-sm font-medium flex-1">{section.label}</span>
                        <Icon
                          icon={openMenus[section.label] ? 'solar:alt-arrow-down-linear' : 'solar:alt-arrow-right-linear'}
                          className="h-4 w-4 transition-transform duration-200"
                        />
                      </>
                    )}
                  </button>

                  <AnimatePresence>
                    {openMenus[section.label] && isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="ml-8 mt-1 space-y-1 pb-2">
                          {section.children.map((child) => (
                            <NavLink
                              key={child.to}
                              to={child.to}
                              end
                              onClick={closeOnPhone}
                              className={({ isActive: active }) =>
                                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                  active
                                    ? 'bg-primary-black text-white font-medium'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                }`
                              }
                            >
                              <Icon icon={child.icon} className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                              <span className="truncate">{child.label}</span>
                            </NavLink>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                <NavLink
                  to={firstChild.to}
                  end
                  title={isExpanded ? undefined : section.label}
                  aria-label={section.label}
                  onClick={closeOnPhone}
                  className={({ isActive: active }) =>
                    `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      active
                        ? 'bg-gray-100 dark:bg-gray-800/60 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                    } ${!isExpanded ? 'justify-center' : ''}`
                  }
                >
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    pathname === firstChild.to ? 'bg-black text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                  }`}>
                    <Icon icon={section.icon} className="h-5 w-5" aria-hidden="true" />
                  </span>
                  {isExpanded && (
                    <span className="text-sm font-medium">{section.label}</span>
                  )}
                </NavLink>
              )}
            </div>
          );
        })}
      </nav>
    </motion.aside>
  );
};

export default Sidebar;
